// Dictionnaires : chargement, mémoire, abonnements.
//  - le FRANÇAIS est inclus dans le bundle principal (le site français n'attend rien, ne charge
//    aucune autre langue) : il sert aussi de repli à toute clé manquante ailleurs ;
//  - chaque autre langue est chargée à la demande, un fichier (espace de noms) à la fois, et seulement
//    quand cette langue est demandée : un visiteur français ne télécharge jamais l'anglais.
import { DEFAULT_LANG, getLanguage } from './config';
import { createT } from './translate';

// require.context est résolu à la compilation (webpack) ; il n'existe pas sous Jest → le try/catch.
let frContext = null;
let lazyContext = null;
try {
  // Français : tous les espaces, dès le démarrage. (Les fichiers « _quelquechose.json » ne sont pas des espaces.)
  frContext = require.context('./locales/fr', false, /^\.\/[a-z0-9-]+\.json$/);
  // Autres langues : un fichier chargé à la demande par espace ; `fr` en est exclu (déjà ci-dessus).
  lazyContext = require.context('./locales', true, /^\.\/(?!fr\/)[a-z]{2,3}\/[a-z0-9-]+\.json$/, 'lazy');
} catch (e) { /* hors webpack (tests) : aucun dictionnaire */ }
const lazyKeys = lazyContext ? new Set(lazyContext.keys()) : new Set();

const dictionaries = { [DEFAULT_LANG]: {} };
if (frContext) {
  frContext.keys().forEach((k) => {
    // require() d'un JSON renvoie l'objet lui-même (pas d'enveloppe « default »).
    dictionaries[DEFAULT_LANG][k.replace(/^\.\/|\.json$/g, '')] = frContext(k);
  });
}

const pending = new Map();
const listeners = new Set();
let version = 0;

const setDict = (lang, ns, dict) => {
  dictionaries[lang] = { ...(dictionaries[lang] || {}), [ns]: dict };
  version += 1;
  listeners.forEach((fn) => fn());
};

export const getDict = (lang, ns) => (dictionaries[lang] || {})[ns];
export const isLoaded = (lang, ns) => lang === DEFAULT_LANG || (dictionaries[lang] && ns in dictionaries[lang]);
export const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
export const getVersion = () => version;

/** Charge un espace de noms d'une langue (une seule fois). Un espace sans fichier = dictionnaire vide (repli français). */
export const loadNamespace = (lang, ns) => {
  if (isLoaded(lang, ns)) return Promise.resolve();
  const id = `${lang}/${ns}`;
  if (pending.has(id)) return pending.get(id);
  const file = `./${lang}/${ns}.json`;
  if (!lazyKeys.has(file)) { setDict(lang, ns, {}); return Promise.resolve(); }
  const promise = lazyContext(file)
    // import() d'un JSON : selon le mode de webpack, l'objet est le module lui-même ou son export `default`.
    .then((mod) => (mod && mod.__esModule ? mod.default : mod))
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.warn(`[i18n] chargement impossible : ${file}`, error);
      return {}; // repli français plutôt qu'un chargement sans fin
    })
    .then((dict) => { pending.delete(id); setDict(lang, ns, dict); });
  pending.set(id, promise);
  return promise;
};

export const loadNamespaces = (lang, list) => Promise.all(list.map((ns) => loadNamespace(lang, ns)));
export const namespacesLoaded = (lang, list) => list.every((ns) => isLoaded(lang, ns));

/** Traduction dans une langue donnée (hors contexte React) — les espaces doivent être chargés. */
export const translateIn = (lang, key, vars) => createT({ lang, locale: getLanguage(lang).locale, getDict })(key, vars);
export const makeT = (lang) => createT({ lang, locale: getLanguage(lang).locale, getDict });
